/**
 * Post-section survey document schemas and Firebase paths.
 *
 * Defines the Firestore schema for survey responses and the paths
 * used to store them. Shared between the webapp and functions packages.
 */

import {z} from 'zod';
import {
  parseSectionId,
  parseSectionWeekId,
  type SectionId,
  type SectionWeekId,
} from '../../../section/sectionApi';
import type {CourseId, UserId} from '../../../types/branded';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_STUDENT_REFLECTIONS_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

// ============================================================================
// Post-Section Survey Document Schemas
// ============================================================================

/** Schema for individual survey response values. */
export const SurveyResponseValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);

/** Value type for survey responses. */
export type SurveyResponseValue = z.infer<typeof SurveyResponseValueSchema>;

/** Schema for a survey response map (question ID to response value). */
export const PostSectionSurveyResponseSchema = z.record(
  z.string(), // question ID
  SurveyResponseValueSchema, // response value
);

/** Survey response object mapping question IDs to response values. */
export type PostSectionSurveyResponse = z.infer<
  typeof PostSectionSurveyResponseSchema
>;

/**
 * Schema for a post-section survey Firestore document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 * For compile-time branded type safety, use the {@link PostSectionSurveyDocument}
 * interface.
 */
export const PostSectionSurveyDocumentSchema = z
  .object({
    responses: PostSectionSurveyResponseSchema,
    userId: z.string().transform((s): UserId => s),
    sectionId: z
      .string()
      .refine(s => parseSectionId(s) !== undefined, {
        message: 'Invalid sectionId',
      })
      .transform((s): SectionId => parseSectionId(s) as SectionId),
    sectionWeek: z
      .number()
      .refine(n => parseSectionWeekId(n) !== undefined, {
        message: 'Invalid sectionWeekId',
      })
      .transform((n): SectionWeekId => parseSectionWeekId(n) as SectionWeekId),
    /**
     * Denormalized course identifier. Optional for back-compat with reflections
     * written before this field was tracked; new writes always include it.
     * Enables `collectionGroup` queries filtered by course (used by the admin
     * dashboard's rating-histogram aggregation). Firestore rules enforce that
     * the stamped value matches the `{courseId}` path segment so a student
     * cannot spoof their reflection into a different course's stats.
     */
    courseId: z.string().optional(),
  })
  .loose();

/**
 * Post-section survey document containing user responses.
 * For runtime validation, use {@link PostSectionSurveyDocumentSchema}.
 */
export type PostSectionSurveyDocument = z.infer<
  typeof PostSectionSurveyDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to the post-section survey responses collection.
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/studentReflections`
 */
export function getPostSectionSurveyCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_STUDENT_REFLECTIONS_SUBCOLLECTION,
  });
}

/**
 * Constructs the path to a specific post-section survey document.
 * Document path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/studentReflections/{userId}`
 */
export function getPostSectionSurveyDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: UserId;
}): string {
  return `${getPostSectionSurveyCollectionPath({courseId, sectionId, sectionWeek})}/${userId}`;
}

// ============================================================================
// Builders
// ============================================================================

/** Builds a post-section survey document from structured data. */
export function createPostSectionSurveyDocument(data: {
  responses: PostSectionSurveyResponse;
  userId: UserId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  courseId: CourseId;
}): PostSectionSurveyDocument {
  return {
    responses: data.responses,
    userId: data.userId,
    sectionId: data.sectionId,
    sectionWeek: data.sectionWeek,
    courseId: data.courseId,
  };
}
